import Splide from "@splidejs/splide";

const MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const ROOT_ATTRIBUTES = ["id", "role", "aria-label", "aria-labelledby"];

const snapshotRoot = element => ({
  className: element.className,
  attributes: ROOT_ATTRIBUTES.map(name => [name, element.getAttribute(name)]),
});

const restoreRoot = (element, snapshot) => {
  element.className = snapshot.className;

  snapshot.attributes.forEach(([name, value]) => {
    if (value === null) {
      element.removeAttribute(name);
    } else {
      element.setAttribute(name, value);
    }
  });
};

export const vehicleElements = root => ({
  panels: [...root.querySelectorAll("[data-panel]")].filter(panel => !panel.classList.contains("splide__slide--clone")),
  tabs: [...root.querySelectorAll("[data-vehicle]")],
  track: root.querySelector(".splide__track"),
});

export const vehicleSpeed = reduced => (reduced ? 0 : 600);

export const vehicleOptions = (reduced, start = 0) => ({
  type: "loop",
  autoWidth: true,
  perMove: 1,
  arrows: true,
  autoplay: false,
  gap: "calc(20 * var(--vw))",
  pagination: false,
  speed: vehicleSpeed(reduced),
  start,
  arrowPath: "M 0 0",
  i18n: {
    prev: "前の車両を表示",
    next: "次の車両を表示",
    first: "最初の車両を表示",
    last: "最後の車両を表示",
  },
});

export const normalizeVehicleIndex = (index, count) => {
  if (count < 1) return -1;

  return ((index % count) + count) % count;
};

export const syncVehicleTabs = (root, nextIndex, focus = false) => {
  const { panels, tabs } = vehicleElements(root);

  if (!tabs.length || panels.length !== tabs.length) return -1;

  const index = normalizeVehicleIndex(nextIndex, tabs.length);
  const previousIndex = normalizeVehicleIndex(index - 1, tabs.length);
  const followingIndex = normalizeVehicleIndex(index + 1, tabs.length);
  const previousTab = tabs[previousIndex];
  const nextTab = tabs[followingIndex];

  const previousButton = root.querySelector(".splide__arrow--prev");
  const nextButton = root.querySelector(".splide__arrow--next");

  previousButton?.setAttribute("aria-label", `${previousTab.textContent.trim()}を表示`);
  nextButton?.setAttribute("aria-label", `${nextTab.textContent.trim()}を表示`);

  tabs.forEach((tab, tabIndex) => {
    const active = tabIndex === index;

    tab.setAttribute("aria-pressed", String(active));
    tab.tabIndex = active ? 0 : -1;
    tab.classList.toggle("is-active", active);
  });

  if (focus) tabs[index].focus();

  return index;
};

export const showSelectedVehicle = (root, nextIndex, focus = false) => {
  const { panels } = vehicleElements(root);
  const index = syncVehicleTabs(root, nextIndex, focus);

  panels.forEach((panel, panelIndex) => {
    panel.hidden = panelIndex !== index;
  });

  return index;
};

export const showAllVehicles = root => {
  vehicleElements(root).panels.forEach(panel => {
    panel.hidden = false;
  });
};

export const mountVehicle = (element, start, onMove, Slider = Splide, reduced = false) => {
  if (!element) return null;

  showAllVehicles(element);

  const slider = new Slider(element, vehicleOptions(reduced, start)).mount();
  const sync = index => {
    onMove(normalizeVehicleIndex(index, slider.length));
  };

  slider.on("resized", () => sync(slider.index));
  slider.on("move", index => sync(index));
  slider.on("moved", index => sync(index));
  sync(slider.index);

  return slider;
};

export const destroyVehicle = slider => {
  if (slider) slider.destroy(true);

  return null;
};

export const syncVehicleMotion = (slider, reduced) => {
  if (slider) slider.options = { speed: vehicleSpeed(reduced) };

  return slider;
};

export const initGallery = ({ root, motion, Slider = Splide } = {}) => {
  const page = root ?? document;
  const motionQuery = motion ?? window.matchMedia(MOTION_QUERY);
  const galleries = [...page.querySelectorAll("[data-vehicle-gallery]")];
  const cleanups = galleries.map(gallery => {
    const { tabs, track } = vehicleElements(gallery);

    if (!tabs.length || !track) return () => {};

    const rootSnapshot = snapshotRoot(gallery);
    const selected = tabs.findIndex(tab => tab.getAttribute("aria-pressed") === "true");
    let current = syncVehicleTabs(gallery, selected >= 0 ? selected : 0);
    let slider = mountVehicle(
      gallery,
      current,
      index => {
        current = syncVehicleTabs(gallery, index);
      },
      Slider,
      motionQuery.matches,
    );

    const click = event => {
      const tab = event.target.closest("[data-vehicle]");

      if (!tab || !gallery.contains(tab)) return;

      const nextIndex = tabs.indexOf(tab);

      slider?.go(nextIndex);
    };

    const keydown = event => {
      const tab = event.target.closest("[data-vehicle]");

      if (!tab || !gallery.contains(tab)) return;

      const commands = {
        ArrowLeft: current - 1,
        ArrowRight: current + 1,
        End: tabs.length - 1,
        Home: 0,
      };

      if (!(event.key in commands)) return;

      event.preventDefault();
      const nextIndex = normalizeVehicleIndex(commands[event.key], tabs.length);

      tabs[nextIndex].focus();
      slider?.go(nextIndex);
    };

    const updateMotion = () => {
      slider = syncVehicleMotion(slider, motionQuery.matches);
    };

    gallery.addEventListener("click", click);
    gallery.addEventListener("keydown", keydown);
    motionQuery.addEventListener("change", updateMotion);

    return () => {
      gallery.removeEventListener("click", click);
      gallery.removeEventListener("keydown", keydown);
      motionQuery.removeEventListener("change", updateMotion);
      slider = destroyVehicle(slider);
      restoreRoot(gallery, rootSnapshot);
      showSelectedVehicle(gallery, current);
    };
  });

  return () => cleanups.forEach(cleanup => cleanup());
};

if (typeof document !== "undefined" && typeof window !== "undefined") {
  initGallery();
}
